// QuotationVersionHistory.js — same-document minor timeline (1.0 → 1.1 → 1.2).
// Supersede major chain stays on the quotations list (status / supersedes_quote_id).
import { GitCompare, History, Loader2, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  formatApiError,
  interiorQuotationApi,
} from './interiorQuotationApi';

const formatWhen = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrencyDelta = (amount = 0) => {
  const n = Number(amount) || 0;
  const formatted = Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Math.abs(n));
  if (n > 0) return `+${formatted}`;
  if (n < 0) return `−${formatted}`;
  return formatted;
};

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   siteId: string,
 *   quoteId: string,
 *   currentVersion?: string,
 *   invoiceNumber?: string,
 *   initialVersions?: Array,
 *   initialHistory?: Array,
 *   onOpenVersion: (record: object) => void,
 * }} props
 */
const QuotationVersionHistory = ({
  isOpen,
  onClose,
  siteId,
  quoteId,
  currentVersion = '',
  invoiceNumber = '',
  initialVersions = null,
  initialHistory = null,
  onOpenVersion,
}) => {
  const [versions, setVersions] = useState([]);
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(currentVersion || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openingVersion, setOpeningVersion] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [compareFrom, setCompareFrom] = useState('');
  const [compareAgainst, setCompareAgainst] = useState('');
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState('');
  const [diffResult, setDiffResult] = useState(null);

  const load = useCallback(async () => {
    if (!siteId || !quoteId) return;
    setLoading(true);
    setError('');
    try {
      // Prefer dedicated versions index; fall back to detail if needed.
      const index = await interiorQuotationApi.listVersions(siteId, quoteId);
      let nextVersions = index.versions || [];
      let nextCurrent = index.current || currentVersion || '';
      let nextHistory = Array.isArray(initialHistory) ? initialHistory : [];

      if (!nextVersions.length || !nextHistory.length) {
        try {
          const detail = await interiorQuotationApi.getQuotation(siteId, quoteId);
          if (!nextVersions.length) nextVersions = detail.versions || [];
          if (!nextHistory.length) nextHistory = detail.history || [];
          if (!nextCurrent) nextCurrent = detail.version || '';
        } catch {
          // index alone is enough for the timeline
        }
      }

      if (!nextVersions.length && Array.isArray(initialVersions) && initialVersions.length) {
        nextVersions = initialVersions;
      }

      setVersions(nextVersions);
      setHistory(nextHistory);
      setCurrent(nextCurrent);
    } catch (err) {
      setError(formatApiError(err));
      if (Array.isArray(initialVersions) && initialVersions.length) {
        setVersions(initialVersions);
        setCurrent(currentVersion || '');
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, quoteId, currentVersion, initialVersions, initialHistory]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setCompareMode(false);
    setCompareFrom('');
    setCompareAgainst('');
    setDiffResult(null);
    setDiffError('');
    load();
    return undefined;
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const tipLabel = useMemo(
    () => String(current || currentVersion || '').replace(/^v/i, ''),
    [current, currentVersion]
  );

  const handleOpen = async (versionLabel) => {
    if (!onOpenVersion || !siteId || !quoteId) return;
    const label = String(versionLabel || '').replace(/^v/i, '');
    setOpeningVersion(label);
    setError('');
    try {
      const record = await interiorQuotationApi.getVersion(siteId, quoteId, label);
      onOpenVersion(record);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setOpeningVersion('');
    }
  };

  const runDiff = async () => {
    if (!compareFrom || !compareAgainst || compareFrom === compareAgainst) {
      setDiffError('Pick two different versions to compare.');
      return;
    }
    setDiffLoading(true);
    setDiffError('');
    setDiffResult(null);
    try {
      const result = await interiorQuotationApi.getVersionDiff(
        siteId,
        quoteId,
        compareFrom,
        compareAgainst
      );
      setDiffResult(result);
    } catch (err) {
      setDiffError(formatApiError(err));
    } finally {
      setDiffLoading(false);
    }
  };

  if (!isOpen) return null;

  const panel = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quotation-version-history-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h3
              id="quotation-version-history-title"
              className="flex items-center gap-2 text-base font-semibold text-gray-900"
            >
              <History className="h-4 w-4 text-teal-700" />
              Version history
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Same document
              {invoiceNumber ? ` · ${invoiceNumber}` : ''}
              {tipLabel ? ` · tip v${tipLabel}` : ''}
              . Minors only — superseded quotes stay on the list.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close version history"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-gray-50 px-5 py-2">
          <button
            type="button"
            onClick={() => {
              setCompareMode((v) => !v);
              setDiffResult(null);
              setDiffError('');
            }}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
              compareMode
                ? 'bg-teal-50 text-teal-800'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <GitCompare className="h-3.5 w-3.5" />
            {compareMode ? 'Comparing…' : 'Compare'}
          </button>
          {compareMode && (
            <button
              type="button"
              onClick={runDiff}
              disabled={diffLoading || !compareFrom || !compareAgainst}
              className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {diffLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Run diff
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
              Loading versions…
            </div>
          )}

          {error && (
            <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {diffError && (
            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {diffError}
            </div>
          )}

          {diffResult && (
            <div className="mb-4 rounded-xl border border-teal-100 bg-teal-50/60 px-3 py-3 text-sm text-gray-800">
              <p className="font-semibold text-teal-900">
                v{diffResult.from} vs v{diffResult.against}
              </p>
              <ul className="mt-2 space-y-1 text-xs text-gray-700">
                <li>
                  Amount delta:{' '}
                  <span className="font-medium">
                    {formatCurrencyDelta(diffResult.diff.finalAmountDelta)}
                  </span>
                </li>
                {(diffResult.diff.sectionsAdded || []).length > 0 && (
                  <li>Sections added: {diffResult.diff.sectionsAdded.join(', ')}</li>
                )}
                {(diffResult.diff.sectionsRemoved || []).length > 0 && (
                  <li>Sections removed: {diffResult.diff.sectionsRemoved.join(', ')}</li>
                )}
                {(diffResult.diff.sectionsRenamed || []).length > 0 && (
                  <li>Sections renamed: {diffResult.diff.sectionsRenamed.join(', ')}</li>
                )}
                {diffResult.diff.designFeePercentChanged && <li>Design fee % changed</li>}
                {diffResult.diff.gstPercentChanged && <li>GST % changed</li>}
                {diffResult.diff.paymentScheduleChanged && <li>Payment schedule changed</li>}
              </ul>
            </div>
          )}

          {!loading && versions.length === 0 && !error && (
            <p className="py-8 text-center text-sm text-gray-500">
              No saved versions yet. Saves (PUT) append minors on this quote.
            </p>
          )}

          <ul className="space-y-2">
            {versions.map((v) => {
              const label = v.version;
              const isTip = tipLabel && label === tipLabel;
              const selectedForCompare =
                compareFrom === label || compareAgainst === label;
              return (
                <li key={label}>
                  <div
                    className={`flex items-stretch gap-2 rounded-xl border px-3 py-2.5 transition-colors ${
                      selectedForCompare
                        ? 'border-teal-300 bg-teal-50/80'
                        : 'border-gray-200 bg-white hover:border-teal-200 hover:bg-teal-50/40'
                    }`}
                  >
                    {compareMode && (
                      <div className="flex flex-col justify-center gap-1">
                        <label className="flex cursor-pointer items-center gap-1 text-[10px] font-semibold uppercase text-gray-500">
                          <input
                            type="radio"
                            name="compare-from"
                            checked={compareFrom === label}
                            onChange={() => setCompareFrom(label)}
                            className="accent-teal-600"
                          />
                          From
                        </label>
                        <label className="flex cursor-pointer items-center gap-1 text-[10px] font-semibold uppercase text-gray-500">
                          <input
                            type="radio"
                            name="compare-against"
                            checked={compareAgainst === label}
                            onChange={() => setCompareAgainst(label)}
                            className="accent-teal-600"
                          />
                          Against
                        </label>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleOpen(label)}
                      disabled={Boolean(openingVersion)}
                      className="min-w-0 flex-1 text-left disabled:opacity-60"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          v{label}
                          {isTip && (
                            <span className="ml-2 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-teal-800">
                              Current
                            </span>
                          )}
                        </span>
                        {openingVersion === label ? (
                          <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
                        ) : (
                          <span className="text-xs font-medium text-teal-700">Preview</span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {[formatWhen(v.at), v.by].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          {history.length > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-4">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Activity
              </h4>
              <ul className="space-y-1.5">
                {history.map((h, idx) => (
                  <li
                    key={`${h.at}-${h.action}-${idx}`}
                    className="text-xs text-gray-600"
                  >
                    <span className="font-medium text-gray-800">
                      {h.action || 'event'}
                    </span>
                    {h.version ? ` · v${h.version}` : ''}
                    {h.at ? ` · ${formatWhen(h.at)}` : ''}
                    {h.by ? ` · ${h.by}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};

export default QuotationVersionHistory;
